// The living player: the real app runs inside a StageFrame (same-origin
// iframe in stage mode, see ../stage) and performs a Scene's steps while this
// component plays the narration clips and keeps the two in step.
//
// Sequencing (see ../stage/protocol.ts):
//   boot      iframe loads → `academy:ready` → status "ready" (nothing moves;
//             the first Play click is the user gesture browsers need for audio)
//   play i    post `academy:play {i}` → the stage emits `academy:step-start {i}`
//             → this starts step i's clip (or a min-duration timer when the
//             scene has no narration for it)
//   advance   a step is complete when BOTH `academy:step-done {i}` has arrived
//             AND the clip ended (or the timer fired); then play i+1
//   finish    after the last step: status "finished"; Play → `academy:restart`
//   pause     pause the clip + post `academy:pause`; resume plays the clip
//             and posts `academy:play {i}` (the stage resumes its own timers)
//   seek      prev/next/timeline/range: pause, post `academy:seek {i, autoplay}`
//             (the stage reloads and fast-forwards), wait for `academy:ready`,
//             then play i if it was playing
//   error     `academy:error` stops everything and shows an amber notice
//
// Narration comes from the manifest at /academy/narration/<sceneId>/manifest.json; a missing
// manifest means captions-only, where every step runs on its min duration.
import { ArrowsPointingInIcon, ArrowsPointingOutIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon, ChatBubbleBottomCenterTextIcon, SpeakerWaveIcon, SpeakerXMarkIcon } from "@heroicons/react/24/outline";
import { ArrowPathIcon, PauseIcon, PlayIcon } from "@heroicons/react/24/solid";
import cn from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Callout from "../../components/common/Callout";
import { localFallback } from "../assets";
import { captionAt } from "./captions";
import StageFrame, { StageFrameHandle } from "../../components/common/StageFrame";
import StepTimeline from "../../components/common/StepTimeline";
import { formatClock } from "../../components/common/video/types";
import Dropdown from "../../components/Settings/Dropdown";
import { NARRATION_BASE, NARRATION_MANIFEST_BASE, NarrationManifest, Scene, SceneAction, SceneStep } from "../scenes/types";
import { isStageToParent, ParentToStageMessage, stageUrl } from "../stage/protocol";

type Status = "booting" | "ready" | "playing" | "paused" | "seeking" | "finished" | "error";

const DEFAULT_MIN_MS = 1500;
const DEFAULT_CPS = 12;
const GLIDE_MS = 600;
/** If the stage never says step-start (older stage build), start the clip anyway. */
const STEP_START_FALLBACK_MS = 1500;
const RATES = [1, 1.25, 1.5, 2];

/** Rough wall time a step's actions take, for the timeline when there is no clip. */
export function estimateActionsMs(actions: SceneAction[]): number {
  return actions.reduce((n, a) => {
    switch (a.type) {
      case "wait":
        return n + a.ms;
      case "type":
        return n + (a.text.length * 1000) / (a.cps ?? DEFAULT_CPS);
      case "moveTo":
      case "click":
      case "scrollIntoView":
        return n + GLIDE_MS;
      default:
        return n;
    }
  }, 0);
}

export function stepDurationMs(step: SceneStep, manifest: NarrationManifest | null): number {
  const clip = manifest?.steps[step.id];
  if (clip?.durationMs) return clip.durationMs;
  return (step.minDurationMs ?? DEFAULT_MIN_MS) + estimateActionsMs(step.actions);
}

export const humanizeStepId = (id: string) => {
  const s = id.replace(/[-_]+/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

interface Props {
  scene: Scene;
  lessonTitle: string;
  className?: string;
}

const LessonPlayer: React.FC<Props> = ({ scene, lessonTitle, className }) => {
  const stageRef = useRef<StageFrameHandle>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // Maximized = the whole player (stage + transport) fills the screen. It is
  // a fixed overlay, with native fullscreen layered on where the browser has it.
  const [maximized, setMaximized] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const [manifest, setManifest] = useState<NarrationManifest | null>(null);
  const [manifestChecked, setManifestChecked] = useState(false);
  const [status, setStatus] = useState<Status>("booting");
  const [step, setStep] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [rate, setRate] = useState(1);
  const [muted, setMuted] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Handler-side mirrors (message and timer callbacks must not see stale state).
  const statusRef = useRef<Status>("booting");
  const stepRef = useRef(0);
  const stepDoneRef = useRef(false);
  const clipEndedRef = useRef(false);
  // The clip URL already retried from the local staging folder (unpublished).
  const triedLocalRef = useRef<string | null>(null);
  const narrationStartedForRef = useRef(-1);
  const stepStartedAtRef = useRef(0);
  const elapsedBaseRef = useRef(0);
  const pendingAutoplayRef = useRef(false);
  const minTimerRef = useRef<number | null>(null);
  const fallbackTimerRef = useRef<number | null>(null);
  const manifestRef = useRef<NarrationManifest | null>(null);
  const rateRef = useRef(1);
  manifestRef.current = manifest;
  rateRef.current = rate;

  const steps = scene.steps;
  const src = useMemo(() => stageUrl(scene.id, scene.startRoute), [scene.id, scene.startRoute]);

  const durations = useMemo(() => steps.map((s) => stepDurationMs(s, manifest)), [steps, manifest]);
  const boundaries = useMemo(() => {
    const out: number[] = [];
    let acc = 0;
    for (const d of durations) {
      out.push(acc);
      acc += d;
    }
    return out;
  }, [durations]);
  const totalMs = durations.reduce((a, b) => a + b, 0);

  const setStatusBoth = (s: Status) => {
    statusRef.current = s;
    setStatus(s);
  };
  const setStepBoth = (i: number) => {
    stepRef.current = i;
    setStep(i);
  };

  const post = useCallback((msg: ParentToStageMessage) => stageRef.current?.postMessage(msg), []);

  const clipUrlFor = useCallback(
    (i: number): string | null => {
      const entry = manifestRef.current?.steps[steps[i]?.id];
      return entry ? `${NARRATION_BASE}/${scene.id}/${entry.file}` : null;
    },
    [steps, scene.id],
  );

  const clearTimers = () => {
    if (minTimerRef.current) window.clearTimeout(minTimerRef.current);
    if (fallbackTimerRef.current) window.clearTimeout(fallbackTimerRef.current);
    minTimerRef.current = null;
    fallbackTimerRef.current = null;
  };

  // Narration manifest (optional).
  useEffect(() => {
    let cancelled = false;
    fetch(`${NARRATION_MANIFEST_BASE}/${scene.id}/manifest.json`, { cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => {
        if (!cancelled) setManifest(m && m.steps ? (m as NarrationManifest) : null);
      })
      .catch(() => {
        if (!cancelled) setManifest(null);
      })
      .finally(() => {
        if (!cancelled) setManifestChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [scene.id]);

  // ---- step lifecycle -------------------------------------------------------

  const finish = useCallback(() => {
    clearTimers();
    setStatusBoth("finished");
    setElapsedMs(totalMs);
  }, [totalMs]);

  const checkAdvanceRef = useRef<() => void>(() => {});

  const armMinTimer = useCallback((remainingMs: number) => {
    if (minTimerRef.current) window.clearTimeout(minTimerRef.current);
    minTimerRef.current = window.setTimeout(
      () => {
        clipEndedRef.current = true;
        checkAdvanceRef.current();
      },
      Math.max(0, remainingMs / rateRef.current),
    );
  }, []);

  const beginNarration = useCallback(
    (i: number) => {
      if (narrationStartedForRef.current === i) return;
      narrationStartedForRef.current = i;
      stepStartedAtRef.current = Date.now();
      elapsedBaseRef.current = 0;
      if (fallbackTimerRef.current) window.clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
      const audio = audioRef.current;
      const clip = clipUrlFor(i);
      if (audio && clip) {
        audio.src = clip;
        audio.playbackRate = rateRef.current;
        audio.play().catch(() => {
          // Blocked or unplayable: run the step on the clock instead.
          armMinTimer(stepDurationMs(steps[i], manifestRef.current));
        });
      } else {
        armMinTimer(stepDurationMs(steps[i], manifestRef.current));
      }
    },
    [clipUrlFor, armMinTimer, steps],
  );

  const playStep = useCallback(
    (i: number) => {
      clearTimers();
      setStepBoth(i);
      stepDoneRef.current = false;
      clipEndedRef.current = false;
      narrationStartedForRef.current = -1;
      setStatusBoth("playing");
      post({ type: "academy:play", stepIndex: i });
      fallbackTimerRef.current = window.setTimeout(() => beginNarration(i), STEP_START_FALLBACK_MS);
    },
    [post, beginNarration],
  );

  checkAdvanceRef.current = () => {
    if (statusRef.current !== "playing") return;
    if (!stepDoneRef.current || !clipEndedRef.current) return;
    const next = stepRef.current + 1;
    if (next < steps.length) playStep(next);
    else finish();
  };

  const pause = useCallback(() => {
    if (statusRef.current !== "playing") return;
    audioRef.current?.pause();
    if (minTimerRef.current) {
      window.clearTimeout(minTimerRef.current);
      minTimerRef.current = null;
    }
    if (narrationStartedForRef.current === stepRef.current) {
      elapsedBaseRef.current += (Date.now() - stepStartedAtRef.current) * rateRef.current;
    }
    post({ type: "academy:pause" });
    setStatusBoth("paused");
  }, [post]);

  const resume = useCallback(() => {
    const i = stepRef.current;
    if (narrationStartedForRef.current !== i) {
      // Paused before the step's narration began (e.g. parked after a seek).
      playStep(i);
      return;
    }
    setStatusBoth("playing");
    stepStartedAtRef.current = Date.now();
    const audio = audioRef.current;
    if (audio && clipUrlFor(i) && !clipEndedRef.current) {
      audio.play().catch(() => {});
    } else if (!clipEndedRef.current) {
      const total = stepDurationMs(steps[i], manifestRef.current);
      armMinTimer(Math.max(0, total - elapsedBaseRef.current));
    }
    post({ type: "academy:play", stepIndex: i });
    checkAdvanceRef.current();
  }, [playStep, clipUrlFor, armMinTimer, steps, post]);

  const seekTo = useCallback(
    (i: number, autoplay?: boolean) => {
      const target = Math.max(0, Math.min(steps.length - 1, i));
      const wasPlaying = statusRef.current === "playing";
      const shouldPlay = autoplay ?? wasPlaying;
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      }
      clearTimers();
      setStepBoth(target);
      stepDoneRef.current = false;
      clipEndedRef.current = false;
      narrationStartedForRef.current = -1;
      elapsedBaseRef.current = 0;
      pendingAutoplayRef.current = shouldPlay;
      setElapsedMs(boundaries[target] ?? 0);
      setStatusBoth("seeking");
      post({ type: "academy:seek", stepIndex: target, autoplay: shouldPlay });
    },
    [steps.length, boundaries, post],
  );

  const restart = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    }
    clearTimers();
    setStepBoth(0);
    stepDoneRef.current = false;
    clipEndedRef.current = false;
    narrationStartedForRef.current = -1;
    pendingAutoplayRef.current = true;
    setElapsedMs(0);
    setStatusBoth("seeking");
    post({ type: "academy:restart" });
  }, [post]);

  const togglePlay = useCallback(() => {
    switch (statusRef.current) {
      case "ready":
        playStep(stepRef.current);
        break;
      case "playing":
        pause();
        break;
      case "paused":
        resume();
        break;
      case "finished":
        restart();
        break;
      default:
        break;
    }
  }, [playStep, pause, resume, restart]);

  // ---- stage messages -------------------------------------------------------

  const onMessage = useCallback(
    (data: unknown) => {
      if (!isStageToParent(data)) return;
      switch (data.type) {
        case "academy:ready": {
          if (statusRef.current === "booting") {
            setStatusBoth("ready");
          } else if (statusRef.current === "seeking") {
            if (pendingAutoplayRef.current) playStep(stepRef.current);
            else setStatusBoth("paused");
            pendingAutoplayRef.current = false;
          }
          break;
        }
        case "academy:step-start":
          if (data.stepIndex === stepRef.current && statusRef.current === "playing") beginNarration(data.stepIndex);
          break;
        case "academy:step-done":
          if (data.stepIndex === stepRef.current) {
            stepDoneRef.current = true;
            checkAdvanceRef.current();
          }
          break;
        case "academy:done":
          break;
        case "academy:error":
          clearTimers();
          audioRef.current?.pause();
          setError(data.message || "an action failed");
          setStatusBoth("error");
          break;
        default:
          break;
      }
    },
    [playStep, beginNarration],
  );

  // ---- clock ---------------------------------------------------------------

  useEffect(() => {
    if (status !== "playing") return;
    const tick = () => {
      const i = stepRef.current;
      const base = boundaries[i] ?? 0;
      let inStep = 0;
      if (narrationStartedForRef.current === i) {
        const audio = audioRef.current;
        inStep = audio && clipUrlFor(i) ? audio.currentTime * 1000 : elapsedBaseRef.current + (Date.now() - stepStartedAtRef.current) * rateRef.current;
      }
      setElapsedMs(Math.min(base + Math.max(0, inStep), totalMs));
    };
    tick();
    const id = window.setInterval(tick, 200);
    return () => window.clearInterval(id);
  }, [status, boundaries, totalMs, clipUrlFor]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = rate;
  }, [rate]);

  // Unmount: stop the clip and timers.
  useEffect(
    () => () => {
      clearTimers();
      audioRef.current?.pause();
    },
    [],
  );

  // ---- derived -------------------------------------------------------------

  const ready = status !== "booting";
  const busy = status === "booting" || status === "seeking";
  const playing = status === "playing";
  const current = steps[step];
  const hasClip = Boolean(clipUrlFor(step));
  // One sentence at a time: the line follows the position inside the step.
  const stepPositionMs = Math.max(0, elapsedMs - (boundaries[step] ?? 0));
  const captionText = current ? (current.caption ?? captionAt(current.narration, stepPositionMs, durations[step] ?? 0)) : "";
  const showCaption = ready && captionText && (captionsOn || !hasClip);
  const transportDisabled = busy || status === "error" || steps.length === 0;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      if (!transportDisabled) togglePlay();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      if (!transportDisabled && step > 0) seekTo(step - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      if (!transportDisabled && step < steps.length - 1) seekTo(step + 1);
    } else if (e.key === "f" || e.key === "F") {
      e.preventDefault();
      toggleMaximized();
    }
  };

  const onRange = (value: number) => {
    let target = 0;
    for (let i = 0; i < boundaries.length; i++) if (boundaries[i] <= value) target = i;
    if (target !== step || status === "finished") seekTo(target);
  };

  const playTitle = status === "finished" ? "Replay" : playing ? "Pause" : "Play";

  const toggleMaximized = useCallback(() => {
    setMaximized((on) => {
      const next = !on;
      try {
        if (next) void rootRef.current?.requestFullscreen?.().catch(() => {});
        else if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
      } catch {
        // No Fullscreen API (iOS Safari): the fixed overlay is the maximize.
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!maximized) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Esc in native fullscreen is handled by the browser (fullscreenchange
    // below); this covers the overlay-only case.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMaximized(false);
    };
    const onFullscreenChange = () => {
      if (!document.fullscreenElement) setMaximized(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
    };
  }, [maximized]);

  // Leaving the page while fullscreen must not strand the browser there.
  useEffect(
    () => () => {
      if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    },
    [],
  );

  return (
    <div ref={rootRef} className={cn("flex flex-col outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40", maximized ? "fixed inset-0 z-[70] bg-base-100" : "rounded-xl", className)} tabIndex={0} onKeyDown={onKeyDown} aria-label={`${lessonTitle} player`}>
      <audio
        ref={audioRef}
        preload="auto"
        onEnded={() => {
          clipEndedRef.current = true;
          checkAdvanceRef.current();
        }}
        onError={() => {
          // Not in the bucket yet (just generated, not published): try the
          // local staging copy once before giving up on the clip.
          const audio = audioRef.current;
          const alt = audio ? localFallback(audio.src) : null;
          if (audio && alt && triedLocalRef.current !== audio.src && narrationStartedForRef.current === stepRef.current && statusRef.current === "playing") {
            triedLocalRef.current = audio.src;
            audio.src = alt;
            audio.play().catch(() => armMinTimer(stepDurationMs(steps[stepRef.current], manifestRef.current)));
            return;
          }
          // Unplayable clip: finish the step on the clock.
          if (narrationStartedForRef.current === stepRef.current && statusRef.current === "playing") {
            armMinTimer(stepDurationMs(steps[stepRef.current], manifestRef.current));
          }
        }}
      />

      <StageFrame
        ref={stageRef}
        src={src}
        title={scene.title || lessonTitle}
        width={scene.viewport.width}
        height={scene.viewport.height}
        onMessage={onMessage}
        busy={busy}
        busyLabel="Loading the app…"
        className={maximized ? undefined : "rounded-b-none"}
        fill={maximized}
      >
        {showCaption && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center p-3 sm:p-4">
            <p className="m-0 max-w-[85%] rounded-lg bg-black/70 px-3 py-1.5 text-center text-[13px] sm:text-sm leading-snug text-white shadow-lg backdrop-blur-sm" aria-live="polite">
              {captionText}
            </p>
          </div>
        )}
        {status === "ready" && (
          <button
            type="button"
            onClick={togglePlay}
            className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/20 transition-colors"
            aria-label="Play lesson"
          >
            <span className="h-16 w-16 rounded-full bg-blue-500 text-white shadow-lg flex items-center justify-center">
              <PlayIcon className="h-7 w-7 ml-1" />
            </span>
          </button>
        )}
      </StageFrame>

      {/* Transport: same vocabulary as VideoPlayer / AudioBar. */}
      <div className={cn("bg-base-100", maximized ? "border-t border-base-300" : "border border-t-0 border-base-300 rounded-b-xl")}>
        <div className="px-3 py-2 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors disabled:opacity-40"
            disabled={transportDisabled || step === 0}
            onClick={() => seekTo(step - 1)}
            title="Previous step"
          >
            <ChevronDoubleLeftIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors disabled:opacity-40"
            disabled={transportDisabled}
            onClick={togglePlay}
            title={playTitle}
            aria-label={playTitle}
          >
            {status === "finished" ? <ArrowPathIcon className="h-4 w-4" /> : playing ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4 ml-0.5" />}
          </button>
          <button
            type="button"
            className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-colors disabled:opacity-40"
            disabled={transportDisabled || step >= steps.length - 1}
            onClick={() => seekTo(step + 1)}
            title="Next step"
          >
            <ChevronDoubleRightIcon className="h-4 w-4" />
          </button>
          <span className="text-sm tabular-nums text-base-content/70">
            {formatClock(elapsedMs)} / {formatClock(totalMs)}
          </span>
          <div className="relative flex-1 min-w-32 flex items-center h-6">
            <input
              type="range"
              min={0}
              max={Math.max(1, totalMs)}
              value={Math.min(elapsedMs, totalMs)}
              disabled={transportDisabled}
              onChange={(e) => onRange(Number(e.target.value))}
              className="inv-seek w-full"
              style={{ "--p": `${totalMs ? (Math.min(elapsedMs, totalMs) / totalMs) * 100 : 0}%` } as React.CSSProperties}
              aria-label="Seek to step"
            />
          </div>
          <Dropdown size="xs" contentClassName="w-20" options={RATES.map((r) => ({ value: String(r), label: `${r}×` }))} value={String(rate)} onChange={(v) => setRate(Number(v))} />
          <button
            type="button"
            className={cn(
              "h-8 w-8 shrink-0 flex items-center justify-center rounded-full transition-colors",
              muted ? "bg-base-200 text-base-content/60 hover:bg-base-300" : "text-base-content/60 hover:bg-base-200",
            )}
            onClick={() => setMuted((m) => !m)}
            title={muted ? "Unmute narration" : "Mute narration"}
            aria-pressed={muted}
            disabled={!manifest}
          >
            {muted ? <SpeakerXMarkIcon className="h-4 w-4" /> : <SpeakerWaveIcon className="h-4 w-4" />}
          </button>
          <button
            type="button"
            className={cn(
              "h-8 w-8 shrink-0 flex items-center justify-center rounded-full transition-colors",
              captionsOn ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300" : "text-base-content/60 hover:bg-base-200",
            )}
            onClick={() => setCaptionsOn((c) => !c)}
            title={captionsOn ? "Hide captions" : "Show captions"}
            aria-pressed={captionsOn}
          >
            <ChatBubbleBottomCenterTextIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full text-base-content/60 hover:bg-base-200 transition-colors"
            onClick={toggleMaximized}
            title={maximized ? "Exit full screen (Esc)" : "Full screen (F)"}
            aria-label={maximized ? "Exit full screen" : "Full screen"}
            aria-pressed={maximized}
          >
            {maximized ? <ArrowsPointingInIcon className="h-4 w-4" /> : <ArrowsPointingOutIcon className="h-4 w-4" />}
          </button>
        </div>
        <div className="px-3 pb-2">
          <StepTimeline steps={steps.map((s) => ({ id: s.id, label: humanizeStepId(s.id) }))} active={step} isDone={(i) => i < step || status === "finished"} onSelect={(i) => seekTo(i)} disabled={transportDisabled} />
        </div>
        {manifestChecked && !manifest && <p className="px-3 pb-2 m-0 text-[11px] text-base-content/50">No narration for this lesson yet; captions carry the script.</p>}
      </div>

      {status === "error" && (
        <Callout tone="warning" title="Player" className="mt-3">
          <p>
            This lesson's player hit a snag: {error}. The written steps below still apply.
          </p>
        </Callout>
      )}
    </div>
  );
};

export default LessonPlayer;
